import { useState, useEffect } from "react";
import { adminService } from "../../../../services/admin-service";
import { jobService } from "../../../../services/job-service";
import { AdminPageHeader, AdminPanel } from "./admin-ui";

function AdminUpdatesPage() {
    const [status, setStatus] = useState("idle"); // idle, loading, success, error
    const [message, setMessage] = useState("");
    const [categoryCount, setCategoryCount] = useState(null);
    const [loadingCount, setLoadingCount] = useState(true);

    const loadCategoryCount = async () => {
        try {
            setLoadingCount(true);
            const data = await jobService.adminJobCategories();
            setCategoryCount(data.categories?.length || 0);
        } catch (err) {
            console.error("Failed to load category count:", err);
        } finally {
            setLoadingCount(false);
        }
    };

    useEffect(() => {
        loadCategoryCount();
    }, []);

    const handleUpdateDatabase = async () => {
        if (!window.confirm("This will overwrite the current job categories schema and re-sync all hierarchical categories. Proceed?")) {
            return;
        }

        setStatus("loading");
        setMessage("");

        try {
            const res = await adminService.updateDatabaseCategories();
            setStatus("success");
            setMessage(res.message || "Database updated successfully!");
            await loadCategoryCount();
        } catch (err) {
            setStatus("error");
            setMessage(err.message || "An error occurred during database update.");
        }
    };

    return (
        <>
            <AdminPageHeader title="Database Updates" subtitle="Apply schema migrations and sync master data" />

            <div className="row">
                <div className="col-lg-8 col-md-12">
                    <AdminPanel title="System Migrations" icon="fa fa-database">
                        <div className="p-4 rounded-3 border bg-light mb-4 position-relative overflow-hidden">
                            <div className="position-absolute end-0 bottom-0 opacity-10" style={{ fontSize: "120px", transform: "translate(20px, 40px)" }}>
                                <i className="fa fa-database text-primary" />
                            </div>
                            
                            <h4 className="text-primary mb-3">Sync Job Categories to D1</h4>
                            <p className="text-muted mb-4">
                                This tool will update your SQLite/D1 database schema and load all hierarchical job categories and subcategories defined in the system. Existing categories will be safely replaced, and a new <code>subcategory</code> reference column will be verified on the <code>jobs</code> table.
                            </p>

                            <div className="row g-3 mb-4">
                                <div className="col-sm-6">
                                    <div className="bg-white p-3 rounded border shadow-sm">
                                        <div className="text-muted small">Current Job Categories</div>
                                        <div className="fs-3 fw-bold text-dark mt-1">
                                            {loadingCount ? (
                                                <span className="spinner-border spinner-border-sm text-secondary" />
                                            ) : (
                                                categoryCount ?? "Unknown"
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="bg-white p-3 rounded border shadow-sm">
                                        <div className="text-muted small">Target Categories</div>
                                        <div className="fs-3 fw-bold text-success mt-1">241</div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-3">
                                <button 
                                    className="btn btn-primary btn-lg d-flex align-items-center gap-2 shadow-sm"
                                    onClick={handleUpdateDatabase}
                                    disabled={status === "loading"}
                                >
                                    {status === "loading" ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            Updating Database...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-sync-alt" />
                                            Update Job Categories
                                        </>
                                    )}
                                </button>
                                
                                {status === "success" && (
                                    <span className="text-success fw-semibold d-flex align-items-center gap-1">
                                        <i className="fa fa-check-circle" /> Sync Complete
                                    </span>
                                )}
                            </div>
                        </div>

                        {message && (
                            <div className={`alert ${status === "success" ? "alert-success" : "alert-danger"} d-flex align-items-start gap-2 shadow-sm`} role="alert">
                                <i className={`fa ${status === "success" ? "fa-info-circle" : "fa-exclamation-triangle"} mt-1`} />
                                <div>
                                    <h6 className="alert-heading fw-bold mb-1">
                                        {status === "success" ? "Success Notification" : "Error Occurred"}
                                    </h6>
                                    <p className="mb-0 small">{message}</p>
                                </div>
                            </div>
                        )}
                    </AdminPanel>
                </div>
                
                <div className="col-lg-4 col-md-12">
                    <AdminPanel title="System Information" icon="fa fa-info-circle">
                        <ul className="list-group list-group-flush small">
                            <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span className="text-muted">Target Binding</span>
                                <span className="badge bg-secondary font-monospace">env.DB (D1)</span>
                            </li>
                            <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span className="text-muted">Source migration file</span>
                                <span className="font-monospace text-dark text-truncate" style={{ maxWidth: "200px" }}>0013_hierarchical_categories.sql</span>
                            </li>
                            <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span className="text-muted">Status</span>
                                <span className="text-success fw-semibold">Connected</span>
                            </li>
                        </ul>
                    </AdminPanel>
                </div>
            </div>
        </>
    );
}

export default AdminUpdatesPage;
